import React, { useState, useEffect } from 'react';
import adminAPI from '../../services/adminAPI';
import cricketService from '../../services/cricketAPI';
import toast from 'react-hot-toast';
import '../../styles/admin/MatchLockManager.css';
import { useDesktopViewport } from '../../hooks/useDesktopViewport';

const MatchLockManager = () => {
    useDesktopViewport();
    const [lockedMatches, setLockedMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showLockModal, setShowLockModal] = useState(false);
    const [newLock, setNewLock] = useState({
        gmid: '',
        matchName: '',
        reason: ''
    });
    const [liveMatches, setLiveMatches] = useState([]);
    const [loadingMatches, setLoadingMatches] = useState(false);

    useEffect(() => {
        loadLockedMatches();
        loadLiveMatches();
    }, []);

    const loadLockedMatches = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getLockedMatches();
            setLockedMatches(response.data.data || []);
        } catch (error) {
            console.error('Error loading locked matches:', error);
            toast.error('Failed to load locked matches');
        } finally {
            setLoading(false);
        }
    };

    const loadLiveMatches = async () => {
        try {
            setLoadingMatches(true);
            // Use the cricketService to fetch live matches
            const response = await cricketService.getLiveMatches();
            
            console.log('📊 Full API Response:', response);
            
            // Extract matches from the nested structure
            let matches = [];
            
            // Check different possible response structures
            if (response?.sport?.body && Array.isArray(response.sport.body)) {
                // Structure: { sport: { body: [...] } }
                matches = response.sport.body;
                console.log('✅ Found matches in sport.body:', matches.length);
            } else if (response?.data?.sport?.body && Array.isArray(response.data.sport.body)) {
                // Structure: { data: { sport: { body: [...] } } }
                matches = response.data.sport.body;
                console.log('✅ Found matches in data.sport.body:', matches.length);
            } else if (Array.isArray(response)) {
                // Direct array
                matches = response;
                console.log('✅ Found matches as array:', matches.length);
            } else if (response?.matches && Array.isArray(response.matches)) {
                // Structure: { matches: [...] }
                matches = response.matches;
                console.log('✅ Found matches in matches key:', matches.length);
            } else if (response?.data && Array.isArray(response.data)) {
                // Structure: { data: [...] }
                matches = response.data;
                console.log('✅ Found matches in data key:', matches.length);
            } else {
                console.warn('⚠️ Unknown response structure:', response);
                // Try to find any array in the response
                for (const key in response) {
                    if (Array.isArray(response[key])) {
                        matches = response[key];
                        console.log(`✅ Found matches in key "${key}":`, matches.length);
                        break;
                    }
                }
            }
            
            // Filter only matches that are in-play (live) and active
            const liveMatchesOnly = matches.filter(match => 
                match.inPlay === true && 
                match.is_active === true
            );
            
            console.log('📊 Processed live matches:', liveMatchesOnly.length);
            setLiveMatches(liveMatchesOnly.length > 0 ? liveMatchesOnly : matches);
        } catch (error) {
            console.error('Error loading live matches:', error);
            toast.error('Failed to load live matches');
        } finally {
            setLoadingMatches(false);
        }
    };

    const handleLockMatch = async () => {
        if (!newLock.gmid || !newLock.matchName) {
            toast.error('Please select a match');
            return;
        }

        // Check if match is already locked
        const alreadyLocked = lockedMatches.some(m => m.gmid === newLock.gmid);
        if (alreadyLocked) {
            toast.error('This match is already locked');
            return;
        }

        try {
            const response = await adminAPI.lockMatch(newLock);
            if (response.data.success) {
                toast.success(`🔒 Match "${newLock.matchName}" locked successfully!`);
                setShowLockModal(false);
                setNewLock({ gmid: '', matchName: '', reason: '' });
                loadLockedMatches();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to lock match');
        }
    };

    const handleUnlockMatch = async (gmid, matchName) => {
        if (!confirm(`Unlock match "${matchName}"?`)) return;

        try {
            const response = await adminAPI.unlockMatch(gmid);
            if (response.data.success) {
                toast.success(`🔓 Match "${matchName}" unlocked successfully!`);
                loadLockedMatches();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to unlock match');
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get available matches (not locked)
    const availableMatches = liveMatches.filter(match => {
        const gmid = match.matchid?.toString() || match.gmid?.toString() || match.id?.toString();
        return !lockedMatches.some(locked => locked.gmid === gmid);
    });

    return (
        <div className="match-lock-manager">
            <div className="lock-header">
                <h2>🔒 Match Lock Manager</h2>
                <button 
                    className="btn-lock-new"
                    onClick={() => setShowLockModal(true)}
                >
                    + Lock New Match
                </button>
            </div>

            {/* Stats Summary */}
            <div className="lock-stats">
                <div className="stat-card">
                    <span className="stat-label">Total Live Matches</span>
                    <span className="stat-value">{liveMatches.length}</span>
                </div>
                <div className="stat-card locked">
                    <span className="stat-label">Locked Matches</span>
                    <span className="stat-value">{lockedMatches.length}</span>
                </div>
                <div className="stat-card available">
                    <span className="stat-label">Available Matches</span>
                    <span className="stat-value">{availableMatches.length}</span>
                </div>
            </div>

            {/* Locked Matches List */}
            <div className="locked-matches-list">
                <h3>🔒 Locked Matches ({lockedMatches.length})</h3>
                {loading ? (
                    <div className="loading">Loading...</div>
                ) : lockedMatches.length === 0 ? (
                    <div className="no-matches">No matches are currently locked</div>
                ) : (
                    <table className="locked-table">
                        <thead>
                            <tr>
                                <th>Match Name</th>
                                <th>GMID</th>
                                <th>Locked By</th>
                                <th>Locked At</th>
                                <th>Reason</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lockedMatches.map((match) => (
                                <tr key={match._id || match.gmid}>
                                    <td className="match-name">{match.matchName}</td>
                                    <td className="match-gmid">{match.gmid}</td>
                                    <td>{match.lockedBy?.username || 'Unknown'}</td>
                                    <td>{formatDate(match.lockedAt)}</td>
                                    <td className="match-reason">{match.reason || '-'}</td>
                                    <td>
                                        <button
                                            className="btn-unlock"
                                            onClick={() => handleUnlockMatch(match.gmid, match.matchName)}
                                        >
                                            🔓 Unlock
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Lock New Match Modal */}
            {showLockModal && (
                <div className="modal-overlay" onClick={() => setShowLockModal(false)}>
                    <div className="modal lock-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>🔒 Lock a Match</h2>
                            <button className="close-btn" onClick={() => setShowLockModal(false)}>✕</button>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label>Select Match</label>
                                <select
                                    value={newLock.gmid}
                                    onChange={(e) => {
                                        const selected = liveMatches.find(m => {
                                            const gmid = m.matchid?.toString() || m.gmid?.toString() || m.id?.toString();
                                            return gmid === e.target.value;
                                        });
                                        setNewLock({
                                            ...newLock,
                                            gmid: e.target.value,
                                            matchName: selected?.matchName || selected?.name || selected?.matchName || ''
                                        });
                                    }}
                                    disabled={loadingMatches}
                                >
                                    <option value="">
                                        {loadingMatches ? 'Loading matches...' : 'Select a match...'}
                                    </option>
                                    {availableMatches.map((match) => {
                                        const gmid = match.matchid?.toString() || match.gmid?.toString() || match.id?.toString();
                                        const matchName = match.matchName || match.name || 'Unknown Match';
                                        return (
                                            <option key={gmid} value={gmid}>
                                                {matchName} (GMID: {gmid})
                                            </option>
                                        );
                                    })}
                                </select>
                                {liveMatches.length === 0 && !loadingMatches && (
                                    <p className="no-matches-msg">No live matches available. Please refresh.</p>
                                )}
                                {availableMatches.length === 0 && liveMatches.length > 0 && !loadingMatches && (
                                    <p className="no-matches-msg">All live matches are already locked.</p>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Match Name</label>
                                <input
                                    type="text"
                                    value={newLock.matchName}
                                    readOnly
                                    className="readonly-input"
                                    placeholder="Match name will auto-fill"
                                />
                            </div>

                            <div className="form-group">
                                <label>Reason (Optional)</label>
                                <input
                                    type="text"
                                    placeholder="Why is this match being locked?"
                                    value={newLock.reason}
                                    onChange={(e) => setNewLock({ ...newLock, reason: e.target.value })}
                                />
                            </div>

                            {/* Show available matches count */}
                            <div className="available-matches-info">
                                <small>
                                    Available to lock: {availableMatches.length} matches
                                </small>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button 
                                className="cancel-btn" 
                                onClick={() => setShowLockModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="confirm-lock-btn"
                                onClick={handleLockMatch}
                                disabled={!newLock.gmid || loadingMatches}
                            >
                                ✅ Lock Match
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MatchLockManager;